import { useState } from 'react';
import { CheckCircle2, ChevronDown, Circle, ExternalLink, HelpCircle, Loader2 } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import { formatDate } from '../../utils/format';
import { useUpdateComplianceControl } from '../../hooks/useCompliance';
import type { ComplianceControl, ComplianceStatus } from '../../types';

const statusConfig: Record<ComplianceStatus, { label: string; tone: 'success' | 'warning' | 'neutral' | 'danger' }> = {
  not_started: { label: 'Not started', tone: 'neutral' },
  in_progress: { label: 'In progress', tone: 'warning' },
  evidenced: { label: 'Evidenced', tone: 'success' },
  needs_review: { label: 'Needs review', tone: 'danger' },
};

const STATUS_OPTIONS: ComplianceStatus[] = ['not_started', 'in_progress', 'evidenced', 'needs_review'];

export function ControlRow({ control }: { control: ComplianceControl }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(control.status);
  const [evidenceNotes, setEvidenceNotes] = useState(control.evidenceNotes);
  const [evidenceUrl, setEvidenceUrl] = useState(control.evidenceUrl ?? '');
  const [answer, setAnswer] = useState(control.answer ?? '');
  const isQuestion = control.framework === 'cyber_essentials_questions';
  const [owner, setOwner] = useState(control.owner ?? '');
  const [nextReviewDue, setNextReviewDue] = useState(control.nextReviewDue ?? '');

  const update = useUpdateComplianceControl();

  const handleSave = () => {
    update.mutate({
      id: control.id,
      updates: {
        status,
        evidenceNotes,
        ...(isQuestion ? { answer } : {}),
        evidenceUrl: evidenceUrl || null,
        owner: owner || null,
        nextReviewDue: nextReviewDue || null,
        lastReviewed: new Date().toISOString().slice(0, 10),
      },
    });
  };

  return (
    <div className="rounded-lg border border-slate-100 dark:border-slate-800">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 p-3 text-left">
        <Badge tone={statusConfig[control.status].tone}>{statusConfig[control.status].label}</Badge>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800 dark:text-slate-100">{control.title}</span>
        {control.relatedJiraKey && (
          <span className="hidden shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400 sm:inline">
            {control.relatedJiraKey}
          </span>
        )}
        {control.autoSignals && control.autoSignals.length > 0 && (
          <span title={control.autoSignals.map((s) => s.detail).join(' · ')}>
            {control.autoSignals.some((s) => s.healthy === false) ? (
              <Circle size={14} className="fill-rose-500 text-rose-500" />
            ) : control.autoSignals.every((s) => s.healthy === null) ? (
              <HelpCircle size={14} className="text-slate-300 dark:text-slate-600" />
            ) : (
              <CheckCircle2 size={14} className="text-pulse-500" />
            )}
          </span>
        )}
        <ChevronDown size={16} className={cn('shrink-0 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="space-y-3 border-t border-slate-100 p-4 dark:border-slate-800">
          <p className="text-sm text-slate-500 dark:text-slate-400">{control.description}</p>

          {isQuestion && control.answerSource && (
            <Badge tone={control.answerSource === 'pulse' ? 'success' : control.answerSource === 'partial' ? 'warning' : 'neutral'}>
              {control.answerSource === 'pulse' ? 'Pulse evidence' : control.answerSource === 'partial' ? 'Partial evidence' : 'You answer'}
            </Badge>
          )}
          {isQuestion && control.guidance && (
            <div className="rounded-lg bg-pulse-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
              <span className="font-medium">Guidance: </span>
              {control.guidance}
            </div>
          )}
          {isQuestion && (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Drafted answer</span>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={3}
                placeholder="Draft the answer you will give in the assessment"
                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </label>
          )}

          {control.relatedJiraKey && (
            <p className="text-xs text-slate-400">
              Tracked in Jira: <span className="font-medium text-slate-500 dark:text-slate-300">{control.relatedJiraKey}</span>
            </p>
          )}

          {control.autoSignals?.map((signal) => (
            <div
              key={signal.linkedCheckId}
              className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:bg-slate-800/60 dark:text-slate-400"
            >
              Live signal ({signal.linkedCheckId}): {signal.detail}
            </div>
          ))}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ComplianceStatus)}
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {statusConfig[s].label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Owner</span>
              <input
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                placeholder="Who owns this control?"
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Evidence notes</span>
            <textarea
              value={evidenceNotes}
              onChange={(e) => setEvidenceNotes(e.target.value)}
              rows={3}
              placeholder="What evidence exists for this control, and where?"
              className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            />
          </label>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Evidence link</span>
              <input
                value={evidenceUrl}
                onChange={(e) => setEvidenceUrl(e.target.value)}
                placeholder="Link to doc, policy, screenshot..."
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Next review due</span>
              <input
                type="date"
                value={nextReviewDue}
                onChange={(e) => setNextReviewDue(e.target.value)}
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
            </label>
          </div>

          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-slate-400">
              {control.lastReviewed ? `Last reviewed ${formatDate(control.lastReviewed)}` : 'Never reviewed'}
              {control.evidenceUrl && (
                <a href={control.evidenceUrl} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 text-pulse-600 hover:underline dark:text-pulse-400">
                  View evidence <ExternalLink size={10} />
                </a>
              )}
            </div>
            <Button size="sm" onClick={handleSave} disabled={update.isPending}>
              {update.isPending && <Loader2 size={13} className="animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
